export interface TutorialSection {
  heading: string;
  content: string;
  tips?: string[];
  technicalDetails?: string[];
}

export interface Tutorial {
  slug: string;
  title: string;
  description: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  time: string;
  category: string;
  sections: TutorialSection[];
}

export const TUTORIALS: Tutorial[] = [
  {
    slug: 'create-first-ball-simulation',
    title: 'How to Create Your First Ball Simulation',
    description: 'Learn the basics of our Creator Studio, from adding balls to setting up gravity containers.',
    level: 'Beginner',
    time: '5 min',
    category: 'Basics',
    sections: [
      {
        heading: 'Welcome to Creator Studio',
        content: 'The Creator Studio is your canvas for building mesmerizing physics simulations. When you first open the studio, you will see the main viewport with a circular boundary container in the center. This boundary acts as the world for your simulation - balls bounce, collide, and interact within it. Think of it as your stage.'
      },
      {
        heading: 'Adding Your First Balls',
        content: 'Click the "Spawn" button in the left toolbar or press the spacebar to add balls into the simulation. Each ball is affected by gravity and will start falling immediately. You can control the spawn rate, initial velocity, and ball size from the Settings panel on the right side. Start with 10–20 balls to keep things manageable while you learn.',
        tips: [
          'Hold Shift + Click to spawn balls at a specific location within the container.',
          'Use the scroll wheel to zoom in and out of the viewport.',
          'Press R to reset the simulation at any time.'
        ]
      },
      {
        heading: 'Understanding Gravity and Boundaries',
        content: 'Gravity is the core force driving your simulation. By default, it pulls balls downward at 9.8 m/s². You can adjust this value in the Physics panel — try setting it to negative values for an anti-gravity effect, or increase it dramatically for chaotic collisions. The circular boundary keeps all balls contained. You can resize it by dragging the boundary handle or entering exact pixel dimensions.',
        technicalDetails: [
          'Gravity range: -50 to 50 m/s²',
          'Boundary radius range: 50px to 800px',
          'Maximum active balls: 1000 (depends on device performance)'
        ]
      },
      {
        heading: 'Adjusting Ball Properties',
        content: 'Each ball has physical properties that affect how it behaves: Mass determines how much force is needed to change its velocity. Restitution (bounciness) controls how much energy is preserved on each collision — set it to 1.0 for perfect bounces, or 0.3 for dampened, realistic impacts. Friction controls sliding resistance when balls contact the boundary wall.',
        tips: [
          'For "oddly satisfying" content, set restitution to 0.85 and friction to 0.01.',
          'Increase mass for heavier-feeling collisions with more visual impact.',
          'Try different ball sizes within the same simulation for visual variety.'
        ]
      },
      {
        heading: 'Saving and Sharing Your Simulation',
        content: 'Once you are happy with your setup, click the Save icon to store your simulation configuration. You can share it with other creators by exporting the settings file, or use the built-in screen recorder to capture your simulation as a video. The recorder supports 1080p and 4K output at 30fps or 60fps.'
      }
    ]
  },
  {
    slug: 'syncing-physics-to-rhythmic-music',
    title: 'Syncing Physics to Rhythmic Music',
    description: 'Master the Rhythmic Mode to create hypnotic videos that move with the beat.',
    level: 'Intermediate',
    time: '12 min',
    category: 'Rhythm',
    sections: [
      {
        heading: 'What is Rhythmic Mode?',
        content: 'Rhythmic Mode is our signature feature that maps ball collision events to musical notes. When a ball strikes the boundary or another ball, it triggers a sound based on its position, velocity, and the musical scale you have selected. This creates a natural audiovisual harmony where the simulation becomes a musical instrument.'
      },
      {
        heading: 'Choosing a Musical Scale',
        content: 'The Creator Studio supports multiple musical scales including Major, Minor, Pentatonic, Blues, and Chromatic. Each scale changes the "mood" of your simulation. Pentatonic is the most forgiving — almost every collision sounds pleasant. Minor scales create tension and drama, perfect for cinematic content. Select your scale from the Audio panel in the right sidebar.',
        tips: [
          'Pentatonic scale is best for beginners — it is nearly impossible to create dissonant sounds.',
          'Try the Blues scale for a jazzy, relaxed vibe.',
          'Use Chromatic sparingly — it gives full tonal range but can sound chaotic.'
        ]
      },
      {
        heading: 'Mapping Collision Velocity to Volume',
        content: 'By default, harder collisions produce louder notes and softer collisions produce quieter ones. This creates dynamic range in your audio. You can adjust the velocity-to-volume curve in the Audio settings — a linear curve gives predictable results, while a logarithmic curve emphasizes softer impacts, creating a more ambient soundscape.',
        technicalDetails: [
          'Velocity mapping curves: Linear, Logarithmic, Exponential, Step',
          'Volume range: 0dB to -60dB',
          'Note duration range: 50ms to 2000ms'
        ]
      },
      {
        heading: 'Syncing to External Audio Tracks',
        content: 'For social media content, you often want to sync your simulation to a trending song. Use the BPM Sync feature to set the simulation tempo to match your audio track. Enter the BPM of your song, and the spawner will release balls at musically relevant intervals. Combined with the beat-reactive gravity feature, your simulation will pulse in time with the music.',
        tips: [
          'Use a BPM detection tool to find the exact tempo of your chosen track.',
          'Enable "Beat Gravity" to make gravity pulse stronger on each beat.',
          'Record the simulation first, then layer the audio track in your video editor for perfect sync.'
        ]
      },
      {
        heading: 'Advanced: Custom Instrument Mapping',
        content: 'Power users can assign different instruments to different ball colors or sizes. For example, large balls can trigger deep bass drums while small balls play high-pitched chimes. This creates a rich, layered audio experience. Access this through the Advanced Audio panel under "Instrument Mapping".'
      }
    ]
  },
  {
    slug: 'optimizing-for-tiktok-youtube-shorts',
    title: 'Optimizing for TikTok and YouTube Shorts',
    description: 'Expert tips on framing, resolution, and pacing for maximum social engagement.',
    level: 'Advanced',
    time: '10 min',
    category: 'Creator Growth',
    sections: [
      {
        heading: 'The 9:16 Vertical Format',
        content: 'TikTok and YouTube Shorts require vertical video at a 9:16 aspect ratio (1080x1920 pixels). In the Creator Studio, switch to "Portrait Mode" in the export settings. This repositions the circular boundary to fit perfectly within the vertical frame, maximizing visual impact on mobile screens.'
      },
      {
        heading: 'The First 500 Milliseconds',
        content: 'Social media algorithms measure how quickly viewers scroll past your content. The first half-second must grab attention immediately. Start your simulation mid-action — never begin with an empty screen. Pre-load 20–30 balls before recording so the viewer sees dynamic motion from frame one. Use high-contrast neon colors against a dark background for maximum visual pop.',
        tips: [
          'Never start a video with a blank canvas — always pre-spawn balls.',
          'Use the "Neon Pulse" color palette for highest contrast on mobile.',
          'Add a text hook like "Wait for the 3rd bounce" to boost retention.'
        ]
      },
      {
        heading: 'Optimal Video Length',
        content: 'For TikTok, aim for 15–30 seconds. For YouTube Shorts, 30–58 seconds performs best. The sweet spot is creating a satisfying "resolution" moment — like all balls settling into a pattern, or a dramatic escape — right at the end. This creates a dopamine payoff that encourages shares and rewatches.',
        technicalDetails: [
          'TikTok optimal length: 15–30 seconds',
          'YouTube Shorts optimal length: 30–58 seconds',
          'Instagram Reels optimal length: 15–30 seconds',
          'Export at 60fps for smoothest motion on all platforms'
        ]
      },
      {
        heading: 'Color Psychology for Engagement',
        content: 'Different colors trigger different emotional responses. Warm tones (red, orange, gold) create excitement and urgency. Cool tones (blue, cyan, purple) create calm and satisfaction. For "oddly satisfying" content, the most successful creators use a gradient palette that shifts slowly over time, creating a hypnotic effect that holds attention.',
        tips: [
          'Gradient shifts between complementary colors perform 2x better than static colors.',
          'Avoid pure white backgrounds — they cause eye strain on mobile OLED screens.',
          'Use the "Auto Palette" feature to generate harmonious color combinations.'
        ]
      },
      {
        heading: 'Posting Strategy',
        content: 'Post consistently at peak engagement hours for your audience. Use the simulation as your visual hook and pair it with trending audio. Add relevant hashtags like #satisfying, #physics, #asmr, and #oddlysatisfying. Cross-post to TikTok, Instagram Reels, and YouTube Shorts simultaneously to maximize reach.'
      }
    ]
  },
  {
    slug: 'creating-custom-asmr-environments',
    title: 'Creating Custom ASMR Environments',
    description: 'How to use spatial audio and material settings for deep relaxation effects.',
    level: 'Intermediate',
    time: '8 min',
    category: 'ASMR',
    sections: [
      {
        heading: 'Why ASMR Physics Content Works',
        content: 'ASMR (Autonomous Sensory Meridian Response) content triggers tingling sensations through gentle visual and auditory stimuli. Physics simulations are uniquely suited for this because collisions produce natural, rhythmic sounds that feel organic rather than manufactured. The combination of smooth ball motion and soft impact sounds creates a deeply relaxing experience.'
      },
      {
        heading: 'Configuring Material Sounds',
        content: 'Each ball material produces a different collision sound. The Creator Studio offers materials including Glass, Wood, Metal, Rubber, and Marble. Glass produces high-pitched, delicate chimes. Wood creates warm, muted thuds. For ASMR, we recommend mixing Glass and Wood materials — the contrast between sharp and soft sounds creates a pleasing texture.',
        tips: [
          'Glass + Wood is the most popular ASMR material combination.',
          'Reduce collision volume to 30–40% for a softer, more intimate feel.',
          'Enable reverb for a spacious, immersive audio environment.'
        ]
      },
      {
        heading: 'Spatial Audio Settings',
        content: 'Spatial audio positions each collision sound in 3D space based on where the ball is in the viewport. Balls on the left produce sound in the left speaker, creating a surround-sound effect. This dramatically enhances the ASMR experience when listeners use headphones. Enable Spatial Audio in the Audio panel and set the stereo width to 80% for optimal effect.',
        technicalDetails: [
          'Stereo width range: 0% (mono) to 100% (full stereo)',
          'Reverb presets: Room, Hall, Cathedral, Chamber',
          'Recommended headphone mode: Binaural'
        ]
      },
      {
        heading: 'Slow Motion and Time Scale',
        content: 'ASMR content benefits from slower, more deliberate motion. Reduce the time scale to 0.3x–0.5x to make balls float and drift gracefully. This gives viewers time to absorb each collision and anticipate the next one. The slower pace also reduces the number of simultaneous sounds, preventing audio clutter.'
      },
      {
        heading: 'Background Ambiance',
        content: 'Layer a subtle ambient background track beneath your collision sounds. The Creator Studio includes built-in ambient presets like Rain, Forest, and White Noise. These fill the gaps between collisions and maintain a consistent relaxation baseline. Set the ambient volume to 10–15% — it should be felt more than heard.',
        tips: [
          'Rain ambiance pairs well with Glass material sounds.',
          'Forest ambiance works best with Wood and Marble materials.',
          'Use the fade-in/fade-out feature for smooth video transitions.'
        ]
      }
    ]
  },
  {
    slug: 'mastering-escape-mode',
    title: 'Mastering Escape Mode Challenges',
    description: 'Build high-tension scenarios where balls must find gaps in the boundary to escape.',
    level: 'Intermediate',
    time: '10 min',
    category: 'Modes',
    sections: [
      {
        heading: 'What is Escape Mode?',
        content: 'Escape Mode transforms your simulation into a tension-driven narrative. Instead of a complete circular boundary, you define one or more gaps where balls can escape. Viewers watch with anticipation as balls bounce chaotically, waiting for the moment one finally finds the exit. This "will it or won\'t it?" dynamic is the foundation of viral physics content.'
      },
      {
        heading: 'Configuring Escape Gaps',
        content: 'Open the Boundary settings and switch to Escape Mode. You can define between 1 and 8 gaps along the circular boundary. Each gap has a configurable width (in degrees) and position. Start with a single narrow gap (15°) at the top of the circle — this creates maximum tension because balls must reach the highest point to escape.',
        tips: [
          'Smaller gaps = more tension but longer videos. Start with 15° for optimal drama.',
          'Place the gap opposite to the gravity direction for maximum difficulty.',
          'Use multiple small gaps for a "maze-like" experience.'
        ]
      },
      {
        heading: 'Ball Spawn Strategy',
        content: 'In Escape Mode, where and when you spawn balls matters. Spawning from the center creates a starburst pattern. Spawning from one side creates a waterfall effect. For the most dramatic content, spawn a single ball and let the viewer follow its journey. Add more balls gradually to increase chaos and the probability of escape.',
        technicalDetails: [
          'Single-ball mode: Maximum narrative focus, longest escape time',
          'Multi-ball mode: Faster escapes, more chaotic visuals',
          'Burst spawn: Release 50+ balls at once for explosive energy'
        ]
      },
      {
        heading: 'Visual Indicators for Escaped Balls',
        content: 'When a ball escapes, it can trigger a visual celebration — a particle burst, color flash, or trail effect. Configure these in the Effects panel. The most engaging setting is "Trail + Glow" which makes the escaped ball leave a luminous trail as it flies away from the boundary, creating a satisfying visual payoff.'
      },
      {
        heading: 'Scoring and Gamification',
        content: 'For YouTube content, add an on-screen counter showing how many balls have escaped vs. how many remain. This gamification element keeps viewers watching to see the final count. Enable the built-in HUD overlay in the Display settings and position it in the top corner of the frame.'
      }
    ]
  },
  {
    slug: 'advanced-trail-effects',
    title: 'Advanced Trail and Visual Effects',
    description: 'Create stunning motion trails, glow effects, and particle systems for cinematic simulations.',
    level: 'Advanced',
    time: '15 min',
    category: 'Visual Design',
    sections: [
      {
        heading: 'Understanding Trail Rendering',
        content: 'Trails are the ghostly paths that balls leave behind as they move. Instead of seeing a ball at a single point, trails show its recent history, creating geometric patterns that evolve over time. The Creator Studio renders trails using a custom WebGL shader pipeline, allowing for hundreds of simultaneous trail segments without performance loss.'
      },
      {
        heading: 'Trail Decay and Length',
        content: 'Trail Decay controls how quickly the trail fades. A value of 1.0 means the trail never fades — the entire history remains visible. A value of 0.1 means each trail segment fades within a tenth of a second. For clean geometric patterns, use 0.7–0.9. For subtle motion blur, use 0.1–0.3.',
        tips: [
          'Trail Decay 0.8 is the "sweet spot" for most satisfying content.',
          'Combine high decay with low ball count for intricate geometric art.',
          'Set decay to 0.01 for a pure motion-blur effect without visible trails.'
        ],
        technicalDetails: [
          'Trail Decay range: 0.0 (instant fade) to 1.0 (permanent)',
          'Trail rendering: WebGL instanced rendering for performance',
          'Maximum trail segments per ball: 500'
        ]
      },
      {
        heading: 'Glow and Bloom Effects',
        content: 'The Bloom post-processing effect adds a soft luminous glow around bright objects. Enable it in the Effects panel and adjust the intensity. Low bloom (0.2) adds subtle warmth. High bloom (0.8+) creates a neon, dreamlike aesthetic that is particularly striking on dark backgrounds. Bloom interacts beautifully with trails, making each path look like a laser beam.'
      },
      {
        heading: 'Particle Systems on Collision',
        content: 'When balls collide, you can trigger particle bursts. These tiny fragments spray outward from the impact point, creating a "sparks flying" effect. Configure particle count, color, lifetime, and spread in the Particle panel. For social media content, moderate particle counts (20–50 per collision) add visual excitement without cluttering the frame.',
        tips: [
          'Match particle colors to ball colors for visual coherence.',
          'Use particle gravity to make sparks arc downward naturally.',
          'Disable particles during ASMR recordings — they break the calm aesthetic.'
        ]
      },
      {
        heading: 'Color Gradients Over Time',
        content: 'The Color Timeline feature allows you to define how ball colors shift over the duration of the simulation. Create a gradient keyframe sequence where balls transition from cool blues to warm oranges, or cycle through the rainbow. This temporal color shift adds a cinematic quality that elevates your content beyond static palette simulations.'
      },
      {
        heading: 'Combining Effects for Cinematic Output',
        content: 'The most visually stunning simulations combine multiple effects: trails for geometry, bloom for atmosphere, particles for impact, and color gradients for narrative progression. Layer these effects thoughtfully — too many effects at full intensity can muddy the visual clarity. The principle is: each effect should enhance, not compete.'
      }
    ]
  },
  {
    slug: 'exporting-high-quality-videos',
    title: 'Exporting High-Quality Videos for Social Media',
    description: 'Master the export pipeline to produce crisp 4K footage at 60fps for any platform.',
    level: 'Beginner',
    time: '6 min',
    category: 'Export',
    sections: [
      {
        heading: 'The Export Panel',
        content: 'Access the Export panel by clicking the camera icon in the top toolbar. Here you control every aspect of your video output: resolution, frame rate, format, duration, and quality. The Creator Studio uses an offline rendering pipeline — it captures each frame individually and assembles them into a video file, ensuring every frame is perfectly rendered regardless of your device\'s real-time performance.'
      },
      {
        heading: 'Resolution and Aspect Ratio',
        content: 'Choose from preset resolutions: 720p (HD), 1080p (Full HD), 1440p (2K), and 2160p (4K). For social media, 1080p is the sweet spot between quality and file size. Always match the aspect ratio to your target platform: 9:16 for TikTok/Shorts/Reels, 16:9 for YouTube landscape videos, and 1:1 for Instagram feed posts.',
        technicalDetails: [
          '720p: 1280×720 (landscape) or 720×1280 (portrait)',
          '1080p: 1920×1080 (landscape) or 1080×1920 (portrait)',
          '4K: 3840×2160 (landscape) or 2160×3840 (portrait)',
          'Custom resolution supported up to 4096×4096'
        ]
      },
      {
        heading: 'Frame Rate Selection',
        content: 'Frame rate determines how smooth your video appears. 30fps is standard but can look choppy during fast physics. 60fps is recommended for all physics content — it captures the full fluidity of ball motion and makes collisions look natural. The offline renderer ensures consistent frame timing even if your device cannot maintain 60fps in real-time.',
        tips: [
          'Always export at 60fps for physics content — the difference is dramatic.',
          'TikTok compresses uploads, so starting at 60fps ensures the final result stays smooth.',
          'For slow-motion ASMR content, record at 60fps and slow down to 0.5x in your editor.'
        ]
      },
      {
        heading: 'File Format and Compression',
        content: 'The Creator Studio exports as WebM (VP9 codec) by default, which offers excellent quality at small file sizes. For maximum compatibility, choose MP4 (H.264). If you plan to do color grading in a video editor, export as a PNG image sequence to preserve full quality. Always set the bitrate to "High" for social media content.'
      },
      {
        heading: 'Batch Export and Presets',
        content: 'Save your export settings as presets for quick access. Create separate presets for each platform: "TikTok Vertical 60fps", "YouTube Landscape 4K", "Instagram Square HD". When you have a simulation you love, use batch export to render it in all formats simultaneously. This saves time and ensures consistent output across platforms.'
      }
    ]
  },
  {
    slug: 'building-faceless-content-brand',
    title: 'Building a Faceless Content Brand',
    description: 'Turn physics simulations into a sustainable content business without showing your face.',
    level: 'Advanced',
    time: '14 min',
    category: 'Creator Growth',
    sections: [
      {
        heading: 'Why Faceless Channels Dominate',
        content: 'Faceless content channels are among the fastest-growing category on YouTube and TikTok. They offer complete anonymity, unlimited scalability (no burnout from being on camera), and lower production overhead. Physics simulations are the perfect faceless content format — the visual experience IS the product. No personality, no voiceover, no face required.'
      },
      {
        heading: 'Choosing Your Sub-Niche',
        content: 'Don\'t try to cover all physics content. Specialize in a recognizable sub-niche so the algorithm learns who to show your content to. Popular sub-niches include: Escape Challenges (tension-based), Rhythmic Collisions (music-synced), ASMR Physics (relaxation), Geometric Art (pattern-focused), and Chaos Simulations (high energy). Pick one and own it.',
        tips: [
          'Research which sub-niches have high demand but low supply by browsing hashtags.',
          'Your first 30 videos should all be in the same sub-niche to train the algorithm.',
          'Use your sub-niche keyword in every video title, description, and hashtag.'
        ]
      },
      {
        heading: 'Content Production Pipeline',
        content: 'Establish a repeatable workflow: Design simulation (15 min) → Fine-tune physics (10 min) → Record (5 min) → Export (automated) → Post. With practice, you can produce 3–5 unique videos per day. Use the Creator Studio\'s preset system to maintain visual consistency across your brand. Save your best settings and iterate on them rather than starting from scratch each time.',
        technicalDetails: [
          'Target output: 3–5 videos per day',
          'Optimal session length: 2–3 hours',
          'Content calendar: Plan 2 weeks ahead',
          'A/B test thumbnails and hooks weekly'
        ]
      },
      {
        heading: 'Monetization Paths',
        content: 'Revenue streams for faceless physics channels include: YouTube AdSense (at 1,000+ subscribers), TikTok Creator Fund, brand sponsorships from tech and gaming companies, selling simulation presets on your own storefront, and Patreon/Substack subscriptions where paying members get exclusive simulation files and behind-the-scenes settings breakdowns.'
      },
      {
        heading: 'Scaling with Multiple Channels',
        content: 'Once your first channel is profitable, duplicate the model. Create separate channels for different sub-niches, each with its own visual brand identity. Use the Creator Studio\'s multi-preset system to maintain distinct aesthetics. Many top creators run 3–5 channels simultaneously, each generating independent revenue streams.'
      }
    ]
  },
  {
    slug: 'physics-settings-deep-dive',
    title: 'Physics Settings Deep Dive',
    description: 'A comprehensive reference for every physics parameter and how it affects your simulation.',
    level: 'Intermediate',
    time: '20 min',
    category: 'Physics',
    sections: [
      {
        heading: 'Gravity',
        content: 'Gravity is the constant acceleration applied to all balls every physics frame. Positive values pull downward, negative values push upward. At 0, balls float weightlessly. The default value (9.8) mimics Earth gravity. Extreme values (40+) create fast, chaotic simulations. Low values (1–3) create gentle, floaty motion perfect for ambient content.',
        technicalDetails: [
          'Default: 9.8 m/s²',
          'Range: -50 to 50 m/s²',
          'Direction: Configurable (default: downward)',
          'Supports radial gravity (pulls toward center instead of downward)'
        ]
      },
      {
        heading: 'Restitution (Bounciness)',
        content: 'Restitution defines how much kinetic energy a ball retains after collision. At 1.0, collisions are perfectly elastic — no energy is lost, and balls bounce forever. At 0.0, collisions are perfectly inelastic — balls stop dead on impact. Real-world rubber is around 0.8. For satisfying content, 0.85–0.95 gives the best visual bounce while maintaining realism.',
        tips: [
          'Set to 1.0+ for "super bounce" effects (balls gain energy on collision).',
          'Values below 0.5 make balls feel "dead" — avoid unless intentional.',
          'Different balls can have different restitution values for variety.'
        ]
      },
      {
        heading: 'Friction',
        content: 'Friction controls resistance when balls slide along the boundary wall. High friction (0.5+) makes balls stick and roll. Low friction (0.01) makes balls glide smoothly. Zero friction creates perfectly slippery surfaces. For visual content, low friction produces the most aesthetically pleasing, flowing motion.',
        technicalDetails: [
          'Default: 0.1',
          'Range: 0.0 to 1.0',
          'Applies to ball-wall and ball-ball contacts',
          'Static friction threshold: configurable in advanced settings'
        ]
      },
      {
        heading: 'Air Resistance (Drag)',
        content: 'Air resistance gradually slows balls over time, simulating atmospheric drag. At 0, balls maintain velocity indefinitely (vacuum physics). At high values, balls slow quickly after each bounce. A small amount of drag (0.001–0.01) adds realism without noticeably damping motion. This is particularly useful for long-running simulations where you want balls to eventually settle.'
      },
      {
        heading: 'Ball Mass and Size',
        content: 'Mass affects collision dynamics — heavier balls push lighter ones aside. Size is purely visual by default, but you can link size to mass in the Physics settings for physically accurate behavior. Large balls with high mass create dramatic, impactful collisions. Small balls with low mass create swarm-like behavior. Mix sizes for visual depth.',
        tips: [
          'Link size to mass for physically accurate simulations.',
          'Unlink size from mass for creative control over aesthetics.',
          'Use size variation (random range) for organic-looking simulations.'
        ]
      },
      {
        heading: 'Collision Detection Mode',
        content: 'The Creator Studio offers two collision detection modes: Standard (discrete) checks for overlaps each frame and is fast but can miss collisions at high speeds. Continuous (CCD) traces the full path of each ball between frames, catching every collision regardless of speed. Use CCD when balls move very fast or are very small.',
        technicalDetails: [
          'Standard mode: O(n) per ball, suitable for up to 500 balls',
          'CCD mode: O(n·k) per ball where k = substeps, suitable for up to 200 balls',
          'Substeps range: 1 to 8 (higher = more accurate, slower)',
          'Spatial partitioning: Grid-based, automatically sized'
        ]
      }
    ]
  },
  {
    slug: 'custom-color-palettes-and-themes',
    title: 'Custom Color Palettes and Themes',
    description: 'Design stunning visual themes with custom colors, gradients, and material finishes.',
    level: 'Beginner',
    time: '7 min',
    category: 'Visual Design',
    sections: [
      {
        heading: 'The Color System',
        content: 'Every visual element in the Creator Studio is customizable through the Color panel. You can set individual ball colors, boundary colors, background gradients, trail colors, and particle colors. Colors are defined in HSL (Hue, Saturation, Lightness) for intuitive adjustment. The HSL model makes it easy to create harmonious palettes by keeping saturation and lightness consistent while varying hue.'
      },
      {
        heading: 'Built-in Palettes',
        content: 'The Creator Studio ships with 20+ curated color palettes designed by digital artists. Palettes include Neon Pulse (high-contrast neon on black), Ocean Drift (blues and teals), Sunset Blaze (warm oranges and pinks), Monochrome (grayscale elegance), and Candy Pop (pastel rainbow). Select a palette to instantly apply it to all visual elements.',
        tips: [
          'Neon Pulse is the most popular palette for TikTok content.',
          'Ocean Drift works best for ASMR and relaxation videos.',
          'Monochrome is underrated — it creates sophisticated, minimalist aesthetics.'
        ]
      },
      {
        heading: 'Creating Custom Palettes',
        content: 'Click "Create Palette" in the Color panel to design your own. Add 2–8 colors that will be distributed across your balls. Use the color wheel to pick hues, then fine-tune saturation and lightness. The live preview shows how your palette looks in the active simulation. Save custom palettes to your library for reuse across projects.'
      },
      {
        heading: 'Background Gradients',
        content: 'Backgrounds are not just flat colors — they support multi-stop gradients with configurable direction and type (linear, radial, conic). A dark radial gradient with a subtle colored center creates depth and focus. For social media, dark backgrounds with a slight warm or cool tint outperform pure black because they reduce harshness on OLED screens.',
        technicalDetails: [
          'Gradient types: Linear, Radial, Conic',
          'Maximum gradient stops: 8',
          'Background animation: Supported (slow gradient rotation)',
          'Opacity: 0–100% per stop'
        ]
      },
      {
        heading: 'Material Finishes',
        content: 'Beyond flat color, balls can have material finishes that affect how light interacts with their surface. Options include Matte (flat, no highlights), Glossy (sharp specular highlights), Metallic (reflective, color-shifted highlights), and Glass (transparent with refraction). Material finishes add depth and realism that sets professional content apart from amateur work.',
        tips: [
          'Glossy finish with bloom creates the most eye-catching neon look.',
          'Metallic finish pairs beautifully with dark backgrounds.',
          'Glass material is GPU-intensive — use sparingly on mobile devices.'
        ]
      }
    ]
  }
];
